/**
 * The one scenario the whole page runs on — LANDING_PAGE.md §4.3.
 *
 * "An AI org ships the Q3 report": a human asks in chat → numbers land in the
 * table → the doc is drafted from those rows → it becomes a deck. Same org,
 * same data, four surfaces. The continuity IS the argument for a suite, so
 * every demo imports from here rather than inventing its own fixture.
 *
 * Server-safe: no DOM, no React, no browser APIs. Imported by both the Hono
 * views and the client bundle.
 */

export const ORG = {
  name: 'Kettle & Rye',
  tagline: 'a small coffee importer, mostly run by agents',
} as const

/** Who is in the room. Note the humans/agents mix — this is the point. */
export interface Participant {
  id: string
  name: string
  kind: 'human' | 'agent'
  role: string
}

export const ROSTER: Participant[] = [
  { id: 'sam', name: 'Sam', kind: 'human', role: 'runs the place' },
  { id: 'analyst', name: 'Analyst', kind: 'agent', role: 'pulls and checks numbers' },
  { id: 'writer', name: 'Writer', kind: 'agent', role: 'drafts the reports' },
  { id: 'deck', name: 'Deck', kind: 'agent', role: 'turns reports into slides' },
]

/** The table demo's seed. Field types map to real monkeytab types. */
export interface RegionRow {
  id: string
  region: string
  revenue: number
  growth: number
  owner: string
  status: 'On track' | 'At risk' | 'Ahead'
  updated: string
}

export const REGION_ROWS: RegionRow[] = [
  {
    id: 'rec-1',
    region: 'North',
    revenue: 412_800,
    growth: 0.18,
    owner: 'Sam',
    status: 'Ahead',
    updated: '2026-09-30',
  },
  {
    id: 'rec-2',
    region: 'South',
    revenue: 228_400,
    growth: 0.04,
    owner: 'Analyst',
    status: 'At risk',
    updated: '2026-09-30',
  },
  {
    id: 'rec-3',
    region: 'Highlands',
    revenue: 191_050,
    growth: 0.12,
    owner: 'Analyst',
    status: 'On track',
    updated: '2026-09-29',
  },
  {
    id: 'rec-4',
    region: 'Coast',
    revenue: 96_300,
    growth: -0.03,
    owner: 'Writer',
    status: 'At risk',
    updated: '2026-09-28',
  },
]

/**
 * Column definitions in monkeytab's real shape — `label` (not `name`),
 * PascalCase `FieldType`, `options.choices` as `SelectOption[]`. Verified
 * against `@datasketch/monkeytab@0.6.0`'s index.d.ts, not guessed.
 *
 * Only PUBLIC field types appear here. Audio and Video are private-workspace
 * only and must never show up in a public demo (LANDING_PAGE.md §5.1).
 */
export const REGION_COLUMNS = [
  { id: 'region', label: 'Region', type: 'Text', width: 130 },
  {
    id: 'revenue',
    label: 'Revenue',
    type: 'Number',
    // NumberFieldOptions: format/currencySymbol/thousandsSeparator — there is
    // no `prefix` key. Read off index.d.ts rather than assumed.
    options: {
      format: 'currency',
      currencySymbol: '$',
      currencyCode: 'USD',
      precision: 0,
      thousandsSeparator: true,
    },
    width: 140,
  },
  {
    id: 'growth',
    label: 'Growth',
    type: 'Number',
    options: { format: 'percentage', precision: 0 },
    width: 110,
  },
  { id: 'owner', label: 'Owner', type: 'Text', width: 120 },
  {
    id: 'status',
    label: 'Status',
    type: 'SingleSelect',
    // SingleSelectFieldOptions is { options: SelectOption[] } — nested, and the
    // inner key is `options`, not `choices`.
    options: {
      options: [
        { value: 'On track', label: 'On track', color: '#8ec5a0' },
        { value: 'At risk', label: 'At risk', color: '#e8a09a' },
        { value: 'Ahead', label: 'Ahead', color: '#f0cf7a' },
      ],
    },
    width: 130,
  },
  { id: 'updated', label: 'Updated', type: 'Date', width: 130 },
]

/** The doc demo's seed — real markdown, round-trippable by monkeydoc. */
export const REPORT_MARKDOWN = `# Q3 Report — ${ORG.name}

## Summary

Revenue grew **12%** against Q2, on the back of the North region. Two regions
need attention before Q4 planning closes.

## By region

- **North** — 412,800, up 18%. Ahead of plan.
- **South** — 228,400, up 4%. At risk; the wholesale pipeline stalled.
- **Highlands** — 191,050, up 12%. On track.
- **Coast** — 96,300, down 3%. At risk.

## Risks

Coast is the only shrinking region. See [the risk log](https://example.com/risks)
for the full picture.
`

/** The chat demo's seed. `ev:` ids are illustrative of monkeytalk's real shape. */
export interface Message {
  ev: string
  from: string
  at: string
  body: string
}

export const CONVERSATION: Message[] = [
  {
    ev: 'ev:aff35133',
    from: 'sam',
    at: '09:14',
    body: '@analyst pull the Q3 numbers into a table — by region, with growth.',
  },
  {
    ev: 'ev:4d9f16bf',
    from: 'analyst',
    at: '09:14',
    body: 'Done — 4 regions in the base. Revenue +12% overall. Coast is down 3%, flagging it.',
  },
  {
    ev: 'ev:7c210a94',
    from: 'sam',
    at: '09:16',
    body: '@writer draft the Q3 report off that table. Lead with North.',
  },
  {
    ev: 'ev:1b8e44d0',
    from: 'writer',
    at: '09:17',
    body: 'Draft is in Documents › Q3 Report. Two risks called out. @deck want slides?',
  },
  {
    ev: 'ev:93fa6e11',
    from: 'deck',
    at: '09:18',
    body: 'On it — 6 cards from the report headings. Theme stays yours.',
  },
]

/** The four modules, as the page presents them. §4.4 / §5. */
export interface ModuleSpec {
  id: 'monkeydoc' | 'monkeytab' | 'monkeytalk' | 'monkeycard'
  name: string
  surface: string
  familiar: string
  /** The one thing a competitor can't say about this module. */
  edge: string
  /** What the visitor downloads at the end of the demo (§5, D10). */
  artifact: string
  /** Whether the live demo is wired yet — drives the honest "preview" badge. */
  live: boolean
  /**
   * Sellable today. monkeytalk and monkeycard are announced but cannot be
   * bought, because the shell has no module contract yet — saying "coming soon"
   * is the honest form of that.
   */
  available: boolean
}

export const MODULES: ModuleSpec[] = [
  {
    id: 'monkeydoc',
    name: 'monkeydoc',
    available: true,
    surface: 'Documents',
    familiar: 'Notion · Google Docs',
    // Deliberately says nothing about the on-disk representation. That is an
    // implementation detail, and naming a schema invites people to couple to
    // it. What a reader can act on is the CLI.
    edge: 'Writes plain markdown, and ships a CLI so an agent can read and edit it the way you do.',
    artifact: '.md + .mdoc',
    live: false,
  },
  {
    id: 'monkeytab',
    name: 'monkeytab',
    available: true,
    surface: 'Tables',
    familiar: 'Airtable · Sheets',
    edge: '14 real field types with a pluggable adapter contract. Your data, in your files.',
    artifact: 'rows.csv + config',
    live: true,
  },
  {
    id: 'monkeytalk',
    name: 'monkeytalk',
    available: false,
    surface: 'Chat',
    familiar: 'Slack',
    edge: 'One append-only log in one portable file. Agents sit in the roster as members.',
    artifact: '.mtalk',
    live: false,
  },
  {
    id: 'monkeycard',
    name: 'monkeycard',
    available: false,
    surface: 'Slides',
    familiar: 'PowerPoint · Slides',
    edge: 'Content and look never couple. Swap the theme; the words are untouched.',
    artifact: '.pptx',
    live: false,
  },
]

/*
 * The public family is exactly these four surfaces. Every other monkey* project
 * is INTERNAL substrate and tooling. A "what's coming" rail naming those used to
 * live here; removed 2026-08-12 (jp) — naming internal machinery on a public
 * page invites outsiders to depend on it and tells a customer nothing they can
 * use. Don't reintroduce it; see docs/ORIENTATION.md for which projects are
 * public and which are not.
 */
